import type { ComponentId } from '@highereducation/hen-ui';
import { getBrowserWindow } from '../../cohesion/browser-window';
import { resolveEventRequestContext } from '../../request-context/resolveEventRequestContext';
import type { EventingContext } from '../../types/context';
import type {
  EventingMetadata,
  NormalizedInteraction,
  NormalizedView,
} from '../../types/normalized';

export function transformCustomDimensions(
  customDimensions: Record<string, unknown>,
): Array<{ key: string; value: string }> {
  return Object.entries(customDimensions)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => ({
      key,
      value:
        typeof value === 'string'
          ? value
          : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value),
    }));
}

function elementTypeFromComponentId(componentId: ComponentId): string {
  const suffix = componentId.startsWith('hen.')
    ? componentId.slice(4)
    : componentId;
  return suffix || componentId;
}

function readMetadataString(
  metadata: EventingMetadata | undefined,
  key: string,
  fallback: string,
): string {
  const value = metadata?.[key];
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

export function buildWebElement(
  event: NormalizedInteraction | NormalizedView,
): Record<string, unknown> {
  const metadata = event.metadata;
  const elementType = readMetadataString(
    metadata,
    'elementType',
    elementTypeFromComponentId(event.componentId),
  );

  return {
    elementType,
    name: readMetadataString(metadata, 'name', event.componentId),
    location: readMetadataString(metadata, 'location', ''),
    text: readMetadataString(metadata, 'text', ''),
    position: readMetadataString(metadata, 'position', ''),
    htmlId: readMetadataString(metadata, 'htmlId', event.componentId),
  };
}

export function resolveWebContext(
  context: EventingContext,
): Record<string, unknown> | undefined {
  const cohesionContext = getBrowserWindow()?._Cohesion?.webContext;
  if (cohesionContext) {
    return cohesionContext as Record<string, unknown>;
  }

  if (!context.pageUrl) {
    return undefined;
  }

  return {
    page: {
      url: context.pageUrl,
    },
  };
}

export function buildBaseTagularPayload(
  event: NormalizedInteraction | NormalizedView,
  context: EventingContext,
  schemaType: string,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    '@type': schemaType,
    webElement: buildWebElement(event),
  };

  if (context.correlationId) {
    payload.correlationId = context.correlationId;
  }

  const webContext = resolveWebContext(context);
  if (webContext) {
    payload.webContext = webContext;
  }

  const requestContext = resolveEventRequestContext(context);
  const customDimensions: Record<string, unknown> = {
    experiment: requestContext.experiment,
    bot: requestContext.bot,
  };
  if (context.tenantId) {
    customDimensions.tenantId = context.tenantId;
  }
  if (event.metadata) {
    for (const [key, value] of Object.entries(event.metadata)) {
      if (
        key !== 'elementType' &&
        key !== 'name' &&
        key !== 'location' &&
        key !== 'text' &&
        key !== 'position' &&
        key !== 'htmlId'
      ) {
        customDimensions[key] = value;
      }
    }
  }

  if (Object.keys(customDimensions).length > 0) {
    payload.customDimensions = transformCustomDimensions(customDimensions);
  }

  return payload;
}
